import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const portalApi = axios.create({
  baseURL: `${API_URL}/api/portal`,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — add portal token
portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('portalAccessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — auto-refresh on 401
portalApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem('portalRefreshToken');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_URL}/api/portal/auth/refresh`, { refreshToken });
          localStorage.setItem('portalAccessToken', data.accessToken);
          localStorage.setItem('portalRefreshToken', data.refreshToken);
          original.headers.Authorization = `Bearer ${data.accessToken}`;
          return portalApi(original);
        } catch {
          localStorage.removeItem('portalAccessToken');
          localStorage.removeItem('portalRefreshToken');
          localStorage.removeItem('portalUser');
          window.location.href = '/portal/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const portalLinkMissesApi = {
  // Must live on the portal instance: on the staff one the missing staff token
  // 401s, and that interceptor treats any 401 as a dead staff session — it wiped
  // the page and dropped the customer on our internal sign-in form.
  report: (data) => portalApi.post('/portal/link-misses', data),
};

export const portalAuthApi = {  login: (email, password, trustedDeviceToken) => portalApi.post('/auth/login', { email, password, trustedDeviceToken }),
  verifyCode: (mfaToken, code, rememberDevice) => portalApi.post('/auth/mfa/verify', { mfaToken, code, rememberDevice }),
  resendCode: (mfaToken) => portalApi.post('/auth/mfa/resend', { mfaToken }),
  logout: (refreshToken) => portalApi.post('/auth/logout', { refreshToken }),
  me: () => portalApi.get('/auth/me'),
  setup: (token, password) => portalApi.post('/auth/setup', { token, password }),
  forgotPassword: (email) => portalApi.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => portalApi.post('/auth/reset-password', { token, password }),
};

export const portalActingApi = {
  // Who this person may act for, and who acts for their own company.
  get: () => portalApi.get('/acting'),
};

export const portalTicketsApi = {  list: (params = {}) => portalApi.get('/tickets', { params }),
  get: (id) => portalApi.get(`/tickets/${id}`),
  create: (data) => portalApi.post('/tickets', data),
  reply: (id, data) => portalApi.post(`/tickets/${id}/reply`, data),
  changeStatus: (id, action) => portalApi.post(`/tickets/${id}/status`, { action }),
};

export const portalUploadsApi = {
  uploadFile: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return portalApi.post('/uploads/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  // `/api/portal/uploads/file/<id>` is authenticated, so a plain <a href> or
  // <audio src> sends no bearer token and 401s. Fetch the bytes with the
  // auth-aware instance and play/download from an in-memory object URL.
  fetchBlob: (url) => portalApi.get(url.replace(/^\/api\/portal/, ''), { responseType: 'blob' }),
};

export const portalAssetsApi = {
  list: (params = {}) => portalApi.get('/assets', { params }),
  types: () => portalApi.get('/assets/types'),
};

export const portalInvoicesApi = {
  list: (params = {}) => portalApi.get('/invoices', { params }),
  get: (id) => portalApi.get(`/invoices/${id}`),
  pdf: (id) => portalApi.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

export const portalContractsApi = {
  list: (params = {}) => portalApi.get('/contracts', { params }),
  get: (id) => portalApi.get(`/contracts/${id}`),
};

export const portalProjectsApi = {
  list: (params = {}) => portalApi.get('/projects', { params }),
  get: (id) => portalApi.get(`/projects/${id}`),
};

export const portalKBApi = {
  list: (params = {}) => portalApi.get('/kb', { params }),
  get: (id) => portalApi.get(`/kb/${id}`),
  // Auth-aware blob fetch for KB attachment downloads. `url` is like
  // `/api/portal/kb/attachments/<id>`; portalApi baseURL already ends in
  // `/api/portal`, so strip that prefix.
  fetchBlob: (url) => portalApi.get(url.replace(/^\/api\/portal/, ''), { responseType: 'blob' }),
};

export const portalProfileApi = {
  update: (data) => portalApi.patch('/profile', data),
  changePassword: (data) => portalApi.post('/profile/change-password', data),
};

export const portalBrandingApi = {
  // No client id: the public endpoint deliberately answers only for our own brand, so
  // a client id cannot be used to learn who resells whom (v1.290.0).
  get: () => portalApi.get('/branding'),
  mine: () => portalApi.get('/branding/me'),
};

export const portalUsersManageApi = {
  list: () => portalApi.get('/users'),
  invite: (data) => portalApi.post('/users/invite', data),
  updatePerms: (contactId, data) => portalApi.patch(`/users/${contactId}`, data),
  revokeAccess: (contactId) => portalApi.delete(`/users/${contactId}/access`),
};

export const portalMagicApi = {
  validate: (token) => portalApi.post(`/auth/magic-validate?token=${token}`),
  getTicket: (ticketId, token) => portalApi.get(`/magic-ticket/${ticketId}?token=${token}`),
  reply: (ticketId, token, body) => portalApi.post(`/magic-ticket/${ticketId}/reply?token=${token}`, { body }),
  // Magic-link holders have no JWT — attachments are served by the token-scoped route.
  attachmentUrl: (ticketId, fileId, token) =>
    `${API_URL}/api/portal/magic-ticket/${ticketId}/attachment/${fileId}?token=${encodeURIComponent(token)}`,
};

export default portalApi;
